import { useDispatch } from 'react-redux'
import { useDebouncedCallback } from 'use-debounce'

import type {
  ModifiedChargeSheetLineItemInput,
  RetailOrderLineItem,
  UpdateRetailOrderLineItemInput,
} from '~/api/graphql/generated/types'
import {
  calculatePrepaidColumns,
  getIsRetailOrderLineItem,
} from '~/components/dashboard/invoices/invoiceUtils'
import { editRetailOrderLineItem } from '~/store/actions/finance'
import { editChargeSheetItem } from '~/store/duck/clientFinanceData'
import type { InvoiceLineItem } from '~/types'

// Quantity field supports decimal values. Ops suggested they need enough time for error correction on decimal values, hence increasing debounce time by 500ms (from default 1000ms to 1500ms).
const QUANTITY_FIELD_DEBOUNCE_MS = 1500

/**
 * Hook for handling quantity changes on charge sheet items
 * Supports both InvoiceLineItem and RetailOrderLineItem with debouncing
 * Handles prepaid items with quantity adjustment logic
 *
 * @param item - The line item to edit
 * @returns Handler function that can be called directly from onChange events
 */
export const useChargeItemQuantityEdit = (
  item: InvoiceLineItem | RetailOrderLineItem,
) => {
  const dispatch = useDispatch()
  const isRetailOrderLineItem = getIsRetailOrderLineItem(item)

  // Debounced handler for invoice line items
  const handleEditInvoiceLineItem = useDebouncedCallback(
    (payload: { quantity: number }) => {
      const data: ModifiedChargeSheetLineItemInput = {
        ...payload,
        prepaid: (item as InvoiceLineItem).prepaid,
        id: item.id,
        expectedModification: item.modificationDate,
      }
      dispatch(editChargeSheetItem({ updateItemInput: [data] }))
    },
    QUANTITY_FIELD_DEBOUNCE_MS,
  )

  // Debounced handler for retail order line items
  const handleEditRetailOrderLineItem = useDebouncedCallback(
    (payload: { quantity: number }) => {
      const retailItem = item as RetailOrderLineItem
      const data: UpdateRetailOrderLineItemInput = {
        ...payload,
        id: retailItem.id,
        quantity: payload.quantity,
        declined: retailItem.declined,
        partNumber: retailItem.partNumber,
        producerId: retailItem.producerId,
        expectedModificationDate: retailItem.modificationDate,
        logExpectedModificationDate: retailItem.prescription?.updatedAt,
        autoshipFrequency: retailItem.autoshipFrequency,
        autoshipUnitId: retailItem.autoshipUnit?.id,
      }
      dispatch(editRetailOrderLineItem(data))
    },
    QUANTITY_FIELD_DEBOUNCE_MS,
  )

  return (value: string) => {
    const numericValue = parseFloat(value)

    // Only trigger API call for valid numbers >= minimum
    if (Number.isNaN(numericValue) || numericValue < 0.01) {
      return
    }

    if (isRetailOrderLineItem) {
      handleEditRetailOrderLineItem({ quantity: numericValue })
    } else if ((item as InvoiceLineItem).prepaid) {
      // For prepaid items, calculatePrepaidColumns may adjust quantity
      // when prepaidRemaining would go negative
      const newItem = { ...item, quantity: numericValue } as InvoiceLineItem
      calculatePrepaidColumns(item as InvoiceLineItem, newItem, false)
      handleEditInvoiceLineItem({ quantity: newItem.quantity })
    } else {
      handleEditInvoiceLineItem({ quantity: numericValue })
    }
  }
}
